/**
 * Transaction GraphQL Queries (8 total)
 *
 * Complete query coverage for Transaction primitive:
 * 1. getTransaction - Single transaction by ID
 * 2. listTransactionsByTenant - All transactions for tenant
 * 3. listTransactionsByStatus - Filter by status
 * 4. listTransactionsByWorkflow - Transactions for workflow instance
 * 5. getActiveTransactions - Currently active transactions
 * 6. getFailedTransactions - Failed transactions needing attention
 * 7. getTransactionStatistics - Aggregate metrics
 * 8. getSagaSuccessRate - Formula #7 success rate
 */

import { gql } from '@apollo/client';
import { TRANSACTION_FIELDS, TRANSACTION_STATISTICS_FIELDS } from './fragments';

/**
 * List all transactions for tenant (paginated)
 */
export const LIST_TRANSACTIONS = gql`
  ${TRANSACTION_FIELDS}
  query ListTransactions($tenantId: String!, $limit: Int, $cursor: String) {
    listTransactions(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
      items {
        ...TransactionFields
      }
      total
      nextCursor
      hasMore
    }
  }
`;

/**
 * Get transaction by ID
 */
export const GET_TRANSACTION = gql`
  ${TRANSACTION_FIELDS}
  query GetTransaction($tenantId: String!, $transactionId: String!) {
    getTransaction(tenantId: $tenantId, transactionId: $transactionId) {
      ...TransactionFields
    }
  }
`;

/**
 * List transactions by tenant
 */
export const LIST_TRANSACTIONS_BY_TENANT = gql`
  ${TRANSACTION_FIELDS}
  query ListTransactionsByTenant($tenantId: String!, $limit: Int, $offset: Int) {
    listTransactionsByTenant(tenantId: $tenantId, limit: $limit, offset: $offset) {
      ...TransactionFields
    }
  }
`;

/**
 * List transactions by status
 */
export const LIST_TRANSACTIONS_BY_STATUS = gql`
  ${TRANSACTION_FIELDS}
  query ListTransactionsByStatus(
    $tenantId: String!
    $status: TransactionStatus!
    $limit: Int
  ) {
    listTransactionsByStatus(tenantId: $tenantId, status: $status, limit: $limit) {
      ...TransactionFields
    }
  }
`;

/**
 * List transactions by workflow instance
 */
export const LIST_TRANSACTIONS_BY_WORKFLOW = gql`
  ${TRANSACTION_FIELDS}
  query ListTransactionsByWorkflow($tenantId: String!, $workflowInstanceId: String!) {
    listTransactionsByWorkflow(
      tenantId: $tenantId
      workflowInstanceId: $workflowInstanceId
    ) {
      ...TransactionFields
    }
  }
`;

/**
 * Get active transactions
 */
export const GET_ACTIVE_TRANSACTIONS = gql`
  ${TRANSACTION_FIELDS}
  query GetActiveTransactions($tenantId: String!) {
    getActiveTransactions(tenantId: $tenantId) {
      ...TransactionFields
    }
  }
`;

/**
 * Get failed transactions
 */
export const GET_FAILED_TRANSACTIONS = gql`
  ${TRANSACTION_FIELDS}
  query GetFailedTransactions($tenantId: String!) {
    getFailedTransactions(tenantId: $tenantId) {
      ...TransactionFields
    }
  }
`;

/**
 * Get transaction statistics
 */
export const GET_TRANSACTION_STATISTICS = gql`
  ${TRANSACTION_STATISTICS_FIELDS}
  query GetTransactionStatistics($tenantId: String!) {
    getTransactionStatistics(tenantId: $tenantId) {
      ...TransactionStatisticsFields
    }
  }
`;

/**
 * Get saga success rate (Formula #7)
 */
export const GET_SAGA_SUCCESS_RATE = gql`
  query GetSagaSuccessRate($tenantId: String!) {
    getSagaSuccessRate(tenantId: $tenantId)
  }
`;
